import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, InputNumber, Tag } from "antd";
import { CommentOutlined } from "@ant-design/icons";
import {
  SONIC_PROSPECT_PRICING,
  formatProspectUsd,
  formatProspectUsdTr,
} from "util/prospectBilling";
import {
  getProspectConversationSettings,
  saveProspectConversationSettings,
} from "./prospectService";
import styles from "./style.module.scss";

const MIN_BUDGET = 1;
const MAX_BUDGET = 10000;

function validBudget(value) {
  const n = Number(value);
  return value !== null && value !== "" && Number.isFinite(n) && n >= MIN_BUDGET && n <= MAX_BUDGET;
}

/**
 * Account-wide conversation length: standard conversations are included,
 * extended conversations (opt-in, paid per answer, monthly budget) keep answering longer.
 */
const ConversationsCard = () => {
  const { t, i18n } = useTranslation();
  const money = i18n.language?.startsWith("tr") ? formatProspectUsdTr : formatProspectUsd;
  const [state, setState] = useState(null);
  const [budget, setBudget] = useState(SONIC_PROSPECT_PRICING.extendedDefaultBudget);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const apply = useCallback((res) => {
    setState(res);
    setBudget(res?.extended?.monthly_budget_usd ?? SONIC_PROSPECT_PRICING.extendedDefaultBudget);
  }, []);

  useEffect(() => {
    let stop = false;
    getProspectConversationSettings()
      .then((res) => {
        if (!stop) apply(res);
      })
      .catch(() => {
        if (!stop) setState(null);
      });
    return () => {
      stop = true;
    };
  }, [apply]);

  const save = async (body) => {
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      apply(await saveProspectConversationSettings(body));
      setConsent(false);
      setSaved(true);
    } catch (err) {
      setError(err?.apiMessage || t("sonic_prospect.conv_save_error"));
    } finally {
      setBusy(false);
    }
  };

  if (!state || !state.released) return null;

  const standard = state.standard_answers ?? SONIC_PROSPECT_PRICING.fairUseAnswers;
  const max = state.extended_max_answers ?? SONIC_PROSPECT_PRICING.extendedMaxAnswers;
  const price = money(state.extended_price_usd ?? SONIC_PROSPECT_PRICING.extendedAnswerPrice);
  const extended = state.extended || {};
  const enabled = Boolean(extended.enabled);
  const month = state.month || {};
  const notice = state.today?.notice;
  const capped = Number(state.capped_sessions_7d) || 0;
  const cardOnFile = Boolean(state.card_on_file);
  const budgetOk = validBudget(budget);

  const budgetField = (
    <label>
      <span>{t("sonic_prospect.conv_budget_label")}</span>
      <InputNumber
        min={MIN_BUDGET}
        max={MAX_BUDGET}
        prefix="$"
        value={budget}
        onChange={(value) => {
          setSaved(false);
          setBudget(value);
        }}
        disabled={busy || (!enabled && !cardOnFile)}
        style={{ width: "100%" }}
        aria-label={t("sonic_prospect.conv_budget_label")}
      />
      <span className={styles.billingNote}>
        {t("sonic_prospect.conv_budget_help", {
          min: money(MIN_BUDGET),
          max: money(MAX_BUDGET),
        })}
      </span>
    </label>
  );

  return (
    <section className={styles.conversationsCard} aria-label={t("sonic_prospect.conv_title")}>
      <div className={styles.conversationsHead}>
        <CommentOutlined aria-hidden="true" />
        <h3>{t("sonic_prospect.conv_title")}</h3>
        {enabled ? <Tag color="green">{t("sonic_prospect.conv_extended_on_tag")}</Tag> : null}
      </div>

      {notice === 100 ? (
        <p className={styles.capBanner} role="alert">
          {t("sonic_prospect.conv_day_limit_100")}{" "}
          <a href="mailto:support@pivony.com">support@pivony.com</a>
        </p>
      ) : notice === 80 ? (
        <p className={styles.capBanner} role="status">
          {t("sonic_prospect.conv_day_limit_80")}{" "}
          <a href="mailto:support@pivony.com">support@pivony.com</a>
        </p>
      ) : null}

      {capped > 0 ? (
        <p className={styles.capBanner} role="status">
          {t("sonic_prospect.conv_capped", { sessions: capped })}{" "}
          {t(enabled ? "sonic_prospect.conv_capped_hint_on" : "sonic_prospect.conv_capped_hint_off")}
        </p>
      ) : null}

      <p className={styles.billingNote}>{t("sonic_prospect.conv_standard", { answers: standard })}</p>
      <p className={styles.billingNote}>
        {t("sonic_prospect.conv_extended_body", { max, answers: standard, price })}
      </p>

      {enabled ? (
        <>
          <p className={styles.billingNote}>
            {t("sonic_prospect.conv_month", {
              billed: money(month.billed_usd || 0),
              pending: money(month.pending_usd || 0),
              waived: month.waived_answers || 0,
              budget: money(extended.monthly_budget_usd ?? budget),
            })}
          </p>
          <div className={styles.billingForm}>
            {budgetField}
            <div className={styles.conversationsActions}>
              <Button
                type="primary"
                loading={busy}
                disabled={!budgetOk}
                onClick={() =>
                  save({ enabled: true, confirmed: false, monthly_budget_usd: Number(budget) })
                }
              >
                {t("sonic_prospect.conv_save_budget")}
              </Button>
              <Button
                danger
                disabled={busy}
                onClick={() =>
                  save({
                    enabled: false,
                    confirmed: false,
                    monthly_budget_usd: extended.monthly_budget_usd ?? null,
                  })
                }
              >
                {t("sonic_prospect.conv_turn_off")}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.billingForm}>
          {!cardOnFile ? (
            <p className={styles.billingError}>{t("sonic_prospect.conv_card_required")}</p>
          ) : null}
          {budgetField}
          <Checkbox
            checked={consent}
            disabled={busy || !cardOnFile}
            onChange={(e) => setConsent(e.target.checked)}
          >
            {t("sonic_prospect.conv_consent", { price })}
          </Checkbox>
          {state.terms_url ? (
            <a
              className={styles.billingNote}
              href={state.terms_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("sonic_prospect.conv_terms")}
            </a>
          ) : null}
          <div className={styles.conversationsActions}>
            <Button
              type="primary"
              loading={busy}
              disabled={!consent || !cardOnFile || !budgetOk}
              onClick={() =>
                save({ enabled: true, confirmed: true, monthly_budget_usd: Number(budget) })
              }
            >
              {t("sonic_prospect.conv_turn_on")}
            </Button>
          </div>
        </div>
      )}

      {error ? <p className={styles.billingError}>{error}</p> : null}
      {saved && !error ? <p className={styles.billingNote}>{t("sonic_prospect.conv_saved")}</p> : null}
    </section>
  );
};

export default ConversationsCard;
